import styled, { keyframes } from "styled-components";
import { Container } from "../../styled/CommonStyledComponents";

/**
 * Two-panel lead-in that plays before the AI chat demo: first naming the
 * problem (a fragmented network of scattered sources), then the promise
 * (one searchable view) — mirroring the deepjudge.ai-style intro referenced
 * in the AI Webpage brief, adapted to Majura's palette.
 */

const SCATTERED_TAGS = [
  { label: "Email", x: 10, y: 20, rot: -8 },
  { label: "WhatsApp", x: 72, y: 12, rot: 6 },
  { label: "Customs Portal", x: 40, y: 62, rot: -5 },
  { label: "Excel Sheets", x: 82, y: 58, rot: 10 },
  { label: "WMS Exports", x: 8, y: 70, rot: 7 },
  { label: "Shipping Docs", x: 55, y: 8, rot: -11 }
];

const drift = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const NetworkIntroSection = () => {
  return (
    <Section>
      <Container>
        <Panel>
          <PanelText>
            <Eyebrow>The Problem</Eyebrow>
            <Headline>Your network is strong, but fragmented</Headline>
            <SubCopy>Small gaps in visibility become expensive mistakes.</SubCopy>
          </PanelText>

          <ScatterGraphic aria-hidden="true">
            {SCATTERED_TAGS.map((tag, index) => (
              <ScatterTag
                key={tag.label}
                style={{
                  left: `${tag.x}%`,
                  top: `${tag.y}%`,
                  transform: `rotate(${tag.rot}deg)`,
                  animationDelay: `${index * 0.3}s`
                }}
              >
                {tag.label}
              </ScatterTag>
            ))}
            <ScatterCore>?</ScatterCore>
          </ScatterGraphic>
        </Panel>

        <Panel $reverse>
          <PanelText>
            <Eyebrow>The Answer</Eyebrow>
            <Headline>Your entire network, finally in one view, searchable</Headline>
            <SubCopy>
              See every lane, every account, every alert — across every milestone — from a
              single place. No complex implementation, no data migration. Just your network,
              finally legible.
            </SubCopy>
          </PanelText>

          <SearchGraphic aria-hidden="true">
            <SearchPrompt>Search the way you think to unlock everything</SearchPrompt>
            <SearchBar>Search the way you think</SearchBar>
          </SearchGraphic>
        </Panel>
      </Container>
    </Section>
  );
};

const Section = styled.div`
  width: 100%;
  padding: 168px 0 0;
  background: #fbfcff;

  @media (max-width: 1025px) {
    padding: 120px 0 0;
  }
`;

const Panel = styled.div<{ $reverse?: boolean }>`
  display: flex;
  flex-direction: ${({ $reverse }) => ($reverse ? "row-reverse" : "row")};
  align-items: center;
  gap: 48px;
  padding: 56px 0;

  & + & {
    border-top: 1px solid #edebe5;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 32px;
  }
`;

const PanelText = styled.div`
  flex: 1;
  min-width: 0;
`;

const Eyebrow = styled.p`
  color: #4f79ff;
  font-weight: 600;
  letter-spacing: 1px;
  font-size: 0.85rem;
  text-transform: uppercase;
  margin: 0 0 12px;
`;

const Headline = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #0a1f60;
  margin: 0 0 16px;
  line-height: 1.25;

  @media (max-width: 1025px) {
    font-size: 1.5rem;
  }
`;

const SubCopy = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #4b5566;
  margin: 0;
  max-width: 440px;
`;

const ScatterGraphic = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  height: 280px;
  border-radius: 20px;
  background: linear-gradient(160deg, #eef0ff 0%, #f8f7f2 100%);
  overflow: hidden;

  @media (max-width: 900px) {
    width: 100%;
    height: 220px;
  }
`;

const ScatterTag = styled.span`
  position: absolute;
  padding: 6px 12px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e4eaf6;
  font-size: 0.7rem;
  font-weight: 600;
  color: #4b5566;
  box-shadow: 0 6px 16px rgba(20, 30, 60, 0.08);
  animation: ${drift} 4s ease-in-out infinite;
`;

const ScatterCore = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: 700;
  color: #ffffff;
  background: #4c5cf0;
  box-shadow: 0 0 0 8px rgba(76, 92, 240, 0.12);
`;

const SearchGraphic = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  height: 280px;
  border-radius: 20px;
  padding: 32px;
  background: linear-gradient(160deg, #f3efff 0%, #fbfcff 100%);
  text-align: center;

  @media (max-width: 900px) {
    width: 100%;
    height: auto;
  }
`;

const SearchPrompt = styled.p`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #0a1f60;
`;

const SearchBar = styled.div`
  width: 100%;
  max-width: 320px;
  padding: 14px 20px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #e4eaf6;
  color: #999999;
  font-size: 0.85rem;
  box-shadow: 0 8px 24px rgba(20, 30, 60, 0.08);
`;

export default NetworkIntroSection;
